import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Menu } from "lucide-react";
import { configApi } from "@/api/auth";
import { Sidebar } from "@/components/layout/Sidebar";
import { SidebarGroupsProvider } from "@/components/layout/SidebarGroupsContext";
import { TopBar } from "@/components/layout/TopBar";
import { Breadcrumbs } from "@/components/layout/Breadcrumbs";
import { BreadcrumbsProvider } from "@/components/layout/BreadcrumbsContext";
import { DashboardRoleProvider } from "@/dashboard/DashboardRoleProvider";
import { ImpersonationBanner } from "@/components/impersonation/ImpersonationBanner";
import { useAuth } from "@/hooks/useAuth";
import { useUserPreference } from "@/hooks/preferences/useUserPreference";
import { cn } from "@/design-system/lib/utils";

/**
 * The per-user preference key for the desktop sidebar's collapsed state.
 *
 * <p>`^[a-z0-9-]{1,64}$`, which `StaffUserPreferenceService` enforces — a key
 * outside that shape is rejected at the write, not at the read, so it would
 * fail silently on the first toggle and never on a load.</p>
 */
export const SIDEBAR_COLLAPSED_PREFERENCE = "sidebar-collapsed";

export function AppShell() {
  const { user } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  /**
   * The desktop sidebar's collapsed/expanded state, PERSISTED per user.
   *
   * <p>It was a `useState`, so a specialist who collapsed the rail to get 192px
   * of content back had it come straight back on the next full load — and the
   * whole point of collapsing it is that the page underneath is too narrow,
   * which is a property of their monitor rather than of the route they were on.
   * `StaffUserPreference`'s own documentation names "a collapsed panel" as a
   * thing it is for.</p>
   *
   * <p>`useUserPreference` writes the cache before it persists, so the rail
   * still moves on the click, and a preference that has never been set or
   * cannot be parsed reads as `false` — the behaviour this had before. Nothing
   * blocks on the read: a slow or failed fetch renders expanded, which is the
   * safe default because every navigation target is visible in it.</p>
   */
  const { value: desktopCollapsed, set: setDesktopCollapsed } = useUserPreference<boolean>(
    SIDEBAR_COLLAPSED_PREFERENCE,
    false,
  );

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const sidebarQuery = useQuery({
    queryKey: ["sidebar", user?.id],
    queryFn: () => configApi.sidebar(),
    enabled: !!user,
  });

  return (
    <SidebarGroupsProvider>
     <BreadcrumbsProvider>
      <DashboardRoleProvider>
      <div className="min-h-screen bg-white-warm text-onyx flex">
        <div
          className={cn(
            "fixed inset-0 z-40 bg-onyx/50 md:hidden",
            mobileOpen ? "block" : "hidden"
          )}
          onClick={() => setMobileOpen(false)}
          aria-hidden
        />

        <Sidebar
          entries={sidebarQuery.data ?? []}
          loading={sidebarQuery.isLoading}
          mobileOpen={mobileOpen}
          collapsed={desktopCollapsed}
          onToggleCollapsed={() => setDesktopCollapsed(!desktopCollapsed)}
        />

        <div className="flex-1 min-w-0 flex flex-col">
          {/* Admin User Impersonation — above the TopBar, so it leads every
              staff route for as long as the impersonated session lasts. It
              renders null on an ordinary session. */}
          <ImpersonationBanner />
          <TopBar
            onHamburger={() => setMobileOpen(true)}
            hamburgerIcon={<Menu size={20} strokeWidth={1.5} aria-hidden />}
          />
          <div className="px-space-4 md:px-space-5 py-space-3 border-b border-border-light bg-white-pure">
            <Breadcrumbs />
          </div>
          {/*
            THE CONTENT CAP, and the convention that goes with it.

            `max-w-content` is `tokens.layout.maxContentWidth`, raised from
            1200px to 1600px. At 1200 a 1900px window gave the page a 1136px
            content box and left ~700px of the monitor as empty margin, which is
            what starved every record card on every detail page: three cards
            across 1136px is a 357px card, and a 176px label track inside one
            leaves 123px for the value.

            🔴 THE CAP IS NOT THE ONLY CONSTRAINT, AND ON A LAPTOP IT IS NOT THE
            BINDING ONE. The sidebar is `md:w-64` (256px) and IN FLOW at `md`
            and up, so the content box is `min(1600, viewport - 256) - 64`.
            Measured, sidebar expanded: 1900px window -> 1536px content;
            1700 -> 1380; 1512 -> 1192; **at or below a 1456px window the cap
            does nothing at all**, because the sidebar leaves less than 1200px
            and the old value was never reached either. Collapsing the sidebar
            (`md:w-16`, 64px) moves that crossover to 1264px.

            WHAT A PAGE OWES IN RETURN. A table or a multi-column grid absorbs
            the extra width; a single-column stack of prose, or of label-left /
            control-right rows, does not — at 1536px the label ends up a
            hand-span from the control it governs, and a paragraph runs to
            roughly 170 characters against a comfortable measure of 60-75. Such
            a region caps ITSELF (`max-w-3xl` for a form, `max-w-5xl` for a
            record-row list), following `LeadDetailPage.tsx`, which has done
            exactly this since long before the cap moved. `PageHeader`'s
            `description` already carries `max-w-reading` (720px) for all ~170
            of its consumers, which is why the list of pages needing their own
            cap is three rather than thirty.
          */}
          <main className="flex-1 w-full max-w-content mx-auto p-space-4 md:p-space-5">
            <Outlet />
          </main>
        </div>
      </div>
      </DashboardRoleProvider>
     </BreadcrumbsProvider>
    </SidebarGroupsProvider>
  );
}
